// ControlValveFaceplate.jsx — Unified premium faceplate (PID / Control Valve)
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Activity } from 'lucide-react';
import TagChartPopup from './TagChartPopup';

const STYLE_ID = 'cvfp-v3';
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = `
    @keyframes cvIn      { from{opacity:0;transform:scale(.95) translateY(6px)} to{opacity:1;transform:none} }
    @keyframes cvRipple  { 0%{transform:scale(1);opacity:.8} 100%{transform:scale(3);opacity:0} }
    @keyframes cvBreathe { 0%,100%{opacity:.3} 50%{opacity:.75} }
    @keyframes cvScan    { 0%{top:-50px} 100%{top:200px} }
    .cvfp3-x:hover   { background:rgba(239,68,68,.2)!important; color:#ff7070!important; }
    .cvfp3-trd:hover { opacity:.85; }
  `;
  document.head.appendChild(s);
}

const SVG_NAMES = {
  controlValveHandActuator: 'Control Valve (Hand)',
  handControlValve:         'Hand Control Valve',
  blueControlValve:         'Control Valve (Blue)',
  controlValve3:            'Control Valve 3',
  controlValveDiaphragm:    'Control Valve (Diaphragm)',
  controlValveGrayFitting:  'Control Valve (Gray)',
  controlValue:             'Control Value',
};

const BG  = '#0c111a';
const LBL = '#7b8fa8';

// Slim vertical bar — borderless, glow fill, integrated into flow
function SlimBar({ label, value, min, max, color }) {
  const pct = Math.min(100, Math.max(0, ((value - min) / (max - min || 1)) * 100));
  const displayVal = typeof value === 'number' ? value.toFixed(1) : '--';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
      <span style={{ fontSize: '9px', fontWeight: '700', color, letterSpacing: '1px', textTransform: 'uppercase' }}>
        {label}
      </span>
      <div style={{ position: 'relative', width: 18, height: 80 }}>
        {/* track */}
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.04)', borderRadius: '10px' }} />
        {/* fill */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          height: pct > 0 ? `max(4px, ${pct}%)` : '0px',
          background: `linear-gradient(to top, ${color}, ${color}88)`,
          boxShadow: pct > 0 ? `0 0 10px ${color}55` : 'none',
          borderRadius: '10px',
          transition: 'height .5s cubic-bezier(.16,1,.3,1)',
        }} />
        {/* midpoint tick */}
        <div style={{ position: 'absolute', bottom: '50%', left: '-5px', right: '-5px', height: '1px', background: 'rgba(255,255,255,0.07)' }} />
      </div>
      <span style={{
        fontSize: '11px', fontWeight: '700', color,
        textShadow: `0 0 10px ${color}66`,
        fontVariantNumeric: 'tabular-nums', letterSpacing: '.3px',
        transition: 'all .4s',
      }}>{displayVal}</span>
      <span style={{ fontSize: '8px', color: LBL, letterSpacing: '.2px' }}>{min}–{max}</span>
    </div>
  );
}

export default function ControlValveFaceplate({ comp, tagValues, ValveIconComponent, onClose, darkMode, plantId }) {
  const cfg = comp.controlValveConfig || {};
  const [pos, setPos]             = useState(comp._initPos || { x: 140, y: 100 });
  const [trendOpen, setTrendOpen] = useState(false);
  const dragging = useRef(false);
  const dragRef  = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current || !dragRef.current) return;
      setPos({
        x: Math.max(0, dragRef.current.px + e.clientX - dragRef.current.mx),
        y: Math.max(0, dragRef.current.py + e.clientY - dragRef.current.my),
      });
    };
    const up = () => { dragging.current = false; };
    const onFSChange = () => setPos(prev => ({
      x: Math.min(Math.max(prev.x, 10), window.innerWidth  - 340),
      y: Math.min(Math.max(prev.y, 10), window.innerHeight - 60),
    }));
    document.addEventListener('mousemove', move);
    document.addEventListener('mouseup', up);
    document.addEventListener('fullscreenchange', onFSChange);
    document.addEventListener('webkitfullscreenchange', onFSChange);
    return () => {
      document.removeEventListener('mousemove', move);
      document.removeEventListener('mouseup', up);
      document.removeEventListener('fullscreenchange', onFSChange);
      document.removeEventListener('webkitfullscreenchange', onFSChange);
    };
  }, []);

  const onDrag = (e) => {
    if (e.target.closest('button')) return;
    e.preventDefault();
    dragging.current = true;
    dragRef.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };
  };

  const num = (tagName) => {
    if (!tagName) return null;
    const v = parseFloat(tagValues?.[tagName]);
    return isNaN(v) ? null : v;
  };

  const pvVal  = num(cfg.pv?.tagName);
  const spVal  = num(cfg.sp?.tagName);
  const opVal  = num(cfg.op?.tagName);

  const rawMode   = cfg.mode?.tagName ? tagValues?.[cfg.mode.tagName] : undefined;
  const modeLabel = rawMode == null ? '--' : (parseFloat(rawMode) >= 1 ? 'AUTO' : 'MANUAL');
  const modeColor = modeLabel === 'AUTO' ? '#10b981' : modeLabel === 'MANUAL' ? '#f59e0b' : '#64748b';

  // SC drives all color theming (neon lines, glow, border, ripple)
  const isOpen = opVal !== null && opVal >= 1;
  const SC     = isOpen ? (cfg.openColor || '#10b981') : (cfg.closeColor || '#ef4444');

  const typeName  = SVG_NAMES[comp.type] || comp.label || 'Control Valve';
  const trendTags = [cfg.pv?.tagName, cfg.sp?.tagName, cfg.op?.tagName].filter(Boolean);
  const trendPos  = { x: Math.min(pos.x + 340, window.innerWidth - 585), y: Math.max(pos.y, 10) };

  const plate = (
    <div ref={panelRef} style={{
      position: 'fixed', left: pos.x, top: pos.y, zIndex: 99999,
      width: '340px',
      background: BG,
      borderRadius: '14px',
      border: `1px solid ${SC}30`,
      boxShadow: `0 0 0 1px rgba(0,0,0,.5), 0 0 36px ${SC}14, 0 20px 60px rgba(0,0,0,.75)`,
      overflow: 'hidden',
      userSelect: 'none',
      animation: 'cvIn .2s cubic-bezier(.16,1,.3,1)',
      transition: 'border-color .5s, box-shadow .5s',
      fontFamily: '-apple-system,BlinkMacSystemFont,"Inter","Segoe UI",sans-serif',
    }}>

      {/* neon top line */}
      <div style={{
        height: '1.5px',
        background: `linear-gradient(90deg,transparent,${SC}bb 40%,${SC} 50%,${SC}bb 60%,transparent)`,
        transition: 'background .5s',
      }} />

      {/* ── HEADER ── */}
      <div onMouseDown={onDrag} style={{
        padding: '12px 12px 0',
        display: 'flex', alignItems: 'flex-start', gap: '8px',
        cursor: 'grab',
      }}>
        {/* status dot */}
        <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: SC, flexShrink: 0, marginTop: '5px', transition: 'background .4s' }} />

        {/* label + subtype */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '13px', fontWeight: '700', color: '#dde6f0', letterSpacing: '.2px',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {comp.label || typeName}
          </div>
          <div style={{ fontSize: '9px', color: LBL, letterSpacing: '.5px', textTransform: 'uppercase', marginTop: '1px' }}>
            {typeName}
          </div>
        </div>

        {/* trend button */}
        {trendTags.length > 0 && (
          <button onClick={() => setTrendOpen(v => !v)} title="Trend" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: '28px', height: '28px', borderRadius: '8px',
            border: `1px solid ${trendOpen ? SC : 'rgba(255,255,255,.20)'}`,
            background: trendOpen ? SC + '25' : 'rgba(255,255,255,.08)',
            cursor: 'pointer', transition: 'all .2s', flexShrink: 0,
            padding: 0,
          }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" stroke={trendOpen ? SC : '#ffffff'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}

        {/* close */}
        <button className="cvfp3-x" onClick={onClose} style={{
          width: '28px', height: '28px', borderRadius: '8px',
          border: '1px solid rgba(255,255,255,.20)',
          background: 'rgba(255,255,255,.08)',
          color: '#aab4c0', cursor: 'pointer', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '17px', lineHeight: 1, fontWeight: '300', transition: 'all .15s',
        }}>×</button>
      </div>

      {/* ── BODY: valve icon left + PV/SP/OP bars right ── */}
      <div onMouseDown={onDrag} style={{
        display: 'flex', padding: '14px 12px 6px',
        cursor: 'grab', alignItems: 'center',
      }}>
        {/* LEFT — icon + open/close badge + mode */}
        <div style={{ width: '104px', flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
          <div style={{ position: 'relative', lineHeight: 0 }}>
            {/* breathing glow halo */}
            <div style={{
              position: 'absolute', inset: '-10px', borderRadius: '50%',
              background: `radial-gradient(circle, ${SC}28 0%, transparent 68%)`,
              transition: 'background .5s',
              animation: isOpen ? 'cvBreathe 2.4s ease-in-out infinite' : 'none',
              pointerEvents: 'none',
            }} />
            {/* scan line when open */}
            {isOpen && (
              <div style={{
                position: 'absolute', left: 0, right: 0, height: '32px',
                background: `linear-gradient(180deg,transparent,${SC}18,transparent)`,
                animation: 'cvScan 2.2s linear infinite', pointerEvents: 'none',
              }} />
            )}
            <div style={{ position: 'relative', zIndex: 1 }}>
              {ValveIconComponent ? (
                <ValveIconComponent
                  width={96} height={96}
                  startColor={SC}
                  midColor={SC + 'aa'}
                  endColor={SC + '66'}
                />
              ) : (
                <div style={{
                  width: 76, height: 76, borderRadius: '50%',
                  background: SC + '18', border: `2px solid ${SC}44`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <span style={{ fontSize: '28px', filter: `drop-shadow(0 0 6px ${SC})` }}>⊠</span>
                </div>
              )}
            </div>
          </div>


          {/* mode */}
          <div style={{ textAlign: 'center', lineHeight: 1.4 }}>
            <div style={{ fontSize: '8.5px', color: LBL, letterSpacing: '.3px' }}>Mode</div>
            <div style={{
              fontSize: '11px', fontWeight: '700', color: modeColor,
              textShadow: `0 0 8px ${modeColor}44`, transition: 'color .4s',
            }}>
              {modeLabel}
            </div>
          </div>
        </div>

        {/* RIGHT — three slim bar meters */}
        <div style={{
          flex: 1, display: 'flex', justifyContent: 'space-evenly',
          alignItems: 'flex-end', paddingLeft: '4px', paddingBottom: '4px',
        }}>
          <SlimBar label="PV" value={pvVal ?? 0} min={cfg.pv?.min ?? 0} max={cfg.pv?.max ?? 100} color="#4ade80" />
          <SlimBar label="SP" value={spVal ?? 0} min={cfg.sp?.min ?? 0} max={cfg.sp?.max ?? 100} color="#facc15" />
          <SlimBar label="OP" value={opVal ?? 0} min={cfg.op?.min ?? 0} max={cfg.op?.max ?? 100} color="#60a5fa" />
        </div>
      </div>

      {/* ── VALUE ROW — full-width Process / Setpoint / Output ── */}
      <div onMouseDown={onDrag} style={{
        display: 'flex', padding: '4px 12px 14px',
        cursor: 'grab',
      }}>
        {[
          { lbl: 'Process',  val: pvVal !== null ? pvVal.toFixed(2) : '--', clr: '#4ade80' },
          { lbl: 'Setpoint', val: spVal !== null ? spVal.toFixed(2) : '--', clr: '#facc15' },
          { lbl: 'Output',   val: opVal !== null ? opVal.toFixed(2) : '--', clr: '#60a5fa' },
        ].map(({ lbl, val, clr }, i) => (
          <div key={lbl} style={{
            flex: 1, textAlign: 'center', paddingTop: '8px',
            borderLeft: i > 0 ? '1px solid rgba(255,255,255,.04)' : 'none',
          }}>
            <div style={{
              fontSize: '8.5px', color: LBL, fontWeight: '500',
              letterSpacing: '.5px', textTransform: 'uppercase', marginBottom: '3px',
            }}>{lbl}</div>
            <div style={{
              fontSize: '17px', fontWeight: '800', color: clr, letterSpacing: '.2px', lineHeight: 1,
              textShadow: `0 0 18px ${clr}44`, transition: 'color .4s, text-shadow .4s',
            }}>{val}</div>
          </div>
        ))}
      </div>

      {/* neon bottom line */}
      <div style={{
        height: '1px',
        background: `linear-gradient(90deg,transparent,${SC}55 50%,transparent)`,
        transition: 'background .5s',
      }} />
    </div>
  );

  return (
    <>
      {createPortal(plate, document.body)}
      {trendOpen && trendTags.length > 0 && (
        <TagChartPopup
          tags={trendTags}
          position={trendPos}
          componentLabel={comp.label || 'Control Valve Trend'}
          onClose={() => setTrendOpen(false)}
          darkMode={darkMode}
          plantId={plantId}
        />
      )}
    </>
  );
}
